// «Liquidações» — o que a Pagali deve a cada entidade (por serviço) e aos estafetas.
// Substitui a «Liquidação Promotor» no menu; a bilheteira dos eventos continua no separador
// Eventos (o mesmo ecrã de sempre). Decisões da Linda, 22/10/2026.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid2 as Grid, IconButton, MenuItem, Paper, Snackbar, Stack, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Refresh, PlayArrow, CheckCircle, Payments, Cancel, ListAlt } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import LiquidacaoPromotorPage from '../LiquidacaoPromotor';
import {
  liquidacoesGerais, LinhaResumo, LiquidacaoGeral, EstafetaPendente, LiquidacaoEstafeta,
} from '../../../services/liquidacoes-gerais.service';
import { todayCv } from '../../../utils/date';

const cve = (v: number | string | null | undefined) =>
  `${Number(v ?? 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CVE`;

/** O domingo que fecha a última semana completa (as liquidações semanais vão até aí). */
function ultimoDomingo(): string {
  const d = new Date(`${todayCv()}T12:00:00`);
  const dia = d.getDay(); // 0 = domingo
  d.setDate(d.getDate() - (dia === 0 ? 7 : dia));
  return d.toISOString().slice(0, 10);
}

const ESTADO_COR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  RASCUNHO: 'warning', APROVADA: 'info', PAGA: 'success', ANULADA: 'default',
};
const estadoChip = (e: string) => <Chip size="small" color={ESTADO_COR[e] ?? 'default'} label={e.toLowerCase()} />;

export default function LiquidacoesPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'entidades';
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>Liquidações</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        O que a Pagali deve a cada entidade, por serviço, e aos estafetas. Nas entidades SISP a SISP
        cobra e paga a Pagali — sem comissão.
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setParams({ tab: v })} sx={{ mb: 2 }}>
        <Tab value="entidades" label="Entidades" />
        <Tab value="estafetas" label="Estafetas" />
        <Tab value="eventos" label="Eventos (bilheteira)" />
      </Tabs>
      {tab === 'entidades' && <Entidades />}
      {tab === 'estafetas' && <Estafetas />}
      {tab === 'eventos' && <Box sx={{ mx: -3 }}><LiquidacaoPromotorPage /></Box>}
    </Box>
  );
}

// ── Entidades ─────────────────────────────────────────────────────────────────

function Entidades() {
  const [ate, setAte] = useState(ultimoDomingo());
  const [filtro, setFiltro] = useState('');
  const [fonte, setFonte] = useState<'TODAS' | 'LIGADA' | 'SISP' | 'POR_LIGAR' | 'EVENTOS'>('TODAS');
  const [resumo, setResumo] = useState<LinhaResumo[]>([]);
  const [lista, setLista] = useState<LiquidacaoGeral[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState('');
  const [aGerar, setAGerar] = useState<string | null>(null);
  const [pagar, setPagar] = useState<LiquidacaoGeral | null>(null);
  const [ref, setRef] = useState('');
  const [linhas, setLinhas] = useState<{ l: LiquidacaoGeral; rows: any[] } | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const [r, l] = await Promise.all([liquidacoesGerais.resumo(ate), liquidacoesGerais.listar()]);
      setResumo(r); setLista(l);
    } catch (e: any) {
      setErro(e?.response?.data?.error || e.message);
    }
    setLoading(false);
  }, [ate]);
  useEffect(() => { carregar(); }, [carregar]);

  const nomes = useMemo(() => new Map(resumo.map((r) => [r.entidadeId, r.entidade])), [resumo]);
  const visiveis = resumo
    .filter((r) => fonte === 'TODAS' || r.fonte === fonte)
    .filter((r) => !filtro || `${r.entidade} ${r.servicoNome}`.toLowerCase().includes(filtro.toLowerCase()));
  const totalPorPagar = resumo.reduce((a, r) => a + (r.pendente?.liquido ?? 0), 0);

  const gerar = async (r: LinhaResumo) => {
    setAGerar(`${r.entidadeId}|${r.servico}`); setErro(null);
    try {
      await liquidacoesGerais.gerar(r.entidadeId, r.servico, ate);
      setOk(`Rascunho criado: ${r.entidade} · ${r.servicoNome}`);
      carregar();
    } catch (e: any) { setErro(e?.response?.data?.error || e.message); }
    setAGerar(null);
  };
  const mudar = async (l: LiquidacaoGeral, accao: 'APROVAR' | 'PAGAR' | 'ANULAR', referencia?: string) => {
    setErro(null);
    try {
      await liquidacoesGerais.mudarEstado(l.id, accao, referencia);
      setOk(accao === 'APROVAR' ? 'Aprovada' : accao === 'PAGAR' ? 'Marcada como paga' : 'Anulada — os pedidos voltam a estar por liquidar');
      setPagar(null); setRef('');
      carregar();
    } catch (e: any) { setErro(e?.response?.data?.error || e.message); }
  };

  return (
    <>
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card><CardContent>
            <Typography variant="h5" fontWeight="bold">{cve(totalPorPagar)}</Typography>
            <Typography variant="body2" color="text.secondary">Por liquidar até {ate}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <Card><CardContent>
            <Typography variant="h5" fontWeight="bold">{lista.filter((l) => l.estado === 'RASCUNHO' || l.estado === 'APROVADA').length}</Typography>
            <Typography variant="body2" color="text.secondary">Liquidações em aberto</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <Card><CardContent>
            <Typography variant="h5" fontWeight="bold">{resumo.length}</Typography>
            <Typography variant="body2" color="text.secondary">Entidade × serviço com contrato</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField fullWidth size="small" label="Pesquisar entidade ou serviço" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField select fullWidth size="small" label="Fonte" value={fonte} onChange={(e) => setFonte(e.target.value as any)}>
              <MenuItem value="TODAS">Todas</MenuItem>
              <MenuItem value="LIGADA">Com valores (Food, Shop, Compras, Vouchers)</MenuItem>
              <MenuItem value="EVENTOS">Eventos</MenuItem>
              <MenuItem value="SISP">Remuneradas pela SISP</MenuItem>
              <MenuItem value="POR_LIGAR">Por ligar</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField fullWidth size="small" type="date" label="Até (inclusive)" value={ate}
              onChange={(e) => setAte(e.target.value)} InputLabelProps={{ shrink: true }}
              helperText="Por omissão, o último domingo" />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entidade</TableCell>
              <TableCell>Serviço</TableCell>
              <TableCell align="right">Transacções</TableCell>
              <TableCell align="right">Bruto</TableCell>
              <TableCell align="right">Comissão + IVA</TableCell>
              <TableCell align="right">Entrega (Pagali)</TableCell>
              <TableCell align="right">SISP</TableCell>
              <TableCell align="right">Líquido</TableCell>
              <TableCell>Última</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : visiveis.length === 0 ? (
              <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}>Nada a mostrar</TableCell></TableRow>
            ) : visiveis.map((r) => {
              const p = r.pendente;
              const k = `${r.entidadeId}|${r.servico}`;
              return (
                <TableRow key={k} hover>
                  <TableCell>
                    <Typography fontWeight={600} variant="body2">{r.entidade}</Typography>
                    {r.entidadeSisp && <Chip size="small" variant="outlined" label="SISP" sx={{ mt: 0.5 }} />}
                    {r.semContrato && (
                      <Tooltip title="Há transacções por liquidar mas a entidade não tem contrato neste serviço: usa-se a comissão por omissão. Crie o contrato em Contratos.">
                        <Chip size="small" color="error" variant="outlined" label="sem contrato" sx={{ mt: 0.5, ml: 0.5 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{r.servicoNome}</TableCell>
                  {r.fonte === 'LIGADA' && p ? <>
                    <TableCell align="right">{p.n}</TableCell>
                    <TableCell align="right">{cve(p.bruto)}</TableCell>
                    <TableCell align="right">{cve(p.comissao + p.iva)}</TableCell>
                    <TableCell align="right">{r.servico === 'FOOD' ? cve(p.entrega) : '—'}</TableCell>
                    <TableCell align="right">
                      {cve(p.sisp)}
                      {p.estimadas > 0 && (
                        <Tooltip title={`${p.estimadas} pedido(s) sem o extracto SISP importado: estimado a 0,805% (Vinti4). Importe o extracto em Pagamentos › Reconciliação SISP.`}>
                          <Chip size="small" color="warning" label="est." sx={{ ml: 0.5 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right"><b>{cve(p.liquido)}</b></TableCell>
                  </> : (
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        {r.fonte === 'EVENTOS' && 'Bilheteira — no separador Eventos'}
                        {r.fonte === 'SISP' && 'Remunerado pela SISP: a Pagali não cobra comissão'}
                        {r.fonte === 'POR_LIGAR' && 'Contrato em vigor; o cálculo deste serviço ainda não está ligado'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    {r.ultimaLiquidacao ? String(r.ultimaLiquidacao).slice(0, 10) : '—'}
                    {r.emAberto > 0 && <Chip size="small" color="warning" label={`${r.emAberto} em aberto`} sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell align="right">
                    {r.fonte === 'LIGADA' && p && p.n > 0 && (
                      <Button size="small" variant="contained" startIcon={aGerar === k ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
                        disabled={!!aGerar} onClick={() => gerar(r)}>
                        Gerar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>Liquidações</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Entidade</TableCell>
              <TableCell>Serviço</TableCell>
              <TableCell>Período</TableCell>
              <TableCell align="right">Pedidos</TableCell>
              <TableCell align="right">Líquido</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.length === 0 && (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>Ainda não há liquidações</TableCell></TableRow>
            )}
            {lista.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>{nomes.get(l.entidade_id) ?? l.entidade_id}</TableCell>
                <TableCell>{l.servico}</TableCell>
                <TableCell>{l.periodo_inicio ? `${String(l.periodo_inicio).slice(0, 10)} → ` : 'até '}{String(l.periodo_fim).slice(0, 10)}</TableCell>
                <TableCell align="right">{l.n_transacoes}</TableCell>
                <TableCell align="right"><b>{cve(l.liquido_cve)}</b>{l.sisp_estimadas > 0 && <Chip size="small" color="warning" label="SISP est." sx={{ ml: 0.5 }} />}</TableCell>
                <TableCell>{estadoChip(l.estado)}{l.referencia_banco && <Typography variant="caption" display="block">ref. {l.referencia_banco}</Typography>}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Ver pedidos"><IconButton size="small" onClick={async () => setLinhas({ l, rows: await liquidacoesGerais.linhas(l.id) })}><ListAlt fontSize="small" /></IconButton></Tooltip>
                  {l.estado === 'RASCUNHO' && <Tooltip title="Aprovar"><IconButton size="small" color="info" onClick={() => mudar(l, 'APROVAR')}><CheckCircle fontSize="small" /></IconButton></Tooltip>}
                  {l.estado === 'APROVADA' && <Tooltip title="Marcar como paga"><IconButton size="small" color="success" onClick={() => setPagar(l)}><Payments fontSize="small" /></IconButton></Tooltip>}
                  {(l.estado === 'RASCUNHO' || l.estado === 'APROVADA') && <Tooltip title="Anular"><IconButton size="small" color="error" onClick={() => mudar(l, 'ANULAR')}><Cancel fontSize="small" /></IconButton></Tooltip>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!pagar} onClose={() => setPagar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar como paga</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {pagar && `${nomes.get(pagar.entidade_id) ?? ''} · ${cve(pagar.liquido_cve)}`}
          </Typography>
          <TextField fullWidth autoFocus label="Referência da transferência" value={ref} onChange={(e) => setRef(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagar(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!ref.trim()} onClick={() => pagar && mudar(pagar, 'PAGAR', ref)}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!linhas} onClose={() => setLinhas(null)} maxWidth="md" fullWidth>
        <DialogTitle>Transacções da liquidação</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Transacção</TableCell><TableCell>Data</TableCell><TableCell align="right">Bruto</TableCell>
                <TableCell align="right">Comissão</TableCell><TableCell align="right">IVA</TableCell>
                <TableCell align="right">Entrega</TableCell><TableCell align="right">SISP</TableCell><TableCell align="right">Líquido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linhas?.rows.map((x: any) => (
                <TableRow key={x.id}>
                  <TableCell>{x.origem === 'FOOD_ORDER' ? 'Pedido' : x.origem === 'SHOP_ORDER' ? 'Encomenda' : x.origem === 'MANDATO_COBRANCA' ? 'Cobrança' : 'Voucher'} #{String(x.origem_id).slice(0, 8)}</TableCell>
                  <TableCell>{x.data_ref ? String(x.data_ref).slice(0, 16).replace('T', ' ') : ''}</TableCell>
                  <TableCell align="right">{cve(x.bruto_cve)}</TableCell>
                  <TableCell align="right">{cve(x.comissao_cve)}</TableCell>
                  <TableCell align="right">{cve(x.comissao_iva_cve)}</TableCell>
                  <TableCell align="right">{cve(x.entrega_cve)}</TableCell>
                  <TableCell align="right">{cve(x.sisp_cve)}{Number(x.sisp_estimada) ? ' *' : ''}</TableCell>
                  <TableCell align="right"><b>{cve(x.liquido_cve)}</b></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary">* SISP estimada (sem extracto importado)</Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setLinhas(null)}>Fechar</Button></DialogActions>
      </Dialog>

      <Snackbar open={!!ok} autoHideDuration={3000} onClose={() => setOk('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success">{ok}</Alert>
      </Snackbar>
    </>
  );
}

// ── Estafetas ─────────────────────────────────────────────────────────────────

function Estafetas() {
  const [ate, setAte] = useState(ultimoDomingo());
  const [pend, setPend] = useState<EstafetaPendente[]>([]);
  const [lista, setLista] = useState<LiquidacaoEstafeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState('');
  const [gerando, setGerando] = useState(false);
  const [pagar, setPagar] = useState<LiquidacaoEstafeta | null>(null);
  const [ref, setRef] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const [p, l] = await Promise.all([liquidacoesGerais.estafetasPendentes(ate), liquidacoesGerais.listarEstafetas()]);
      setPend(p); setLista(l);
    } catch (e: any) { setErro(e?.response?.data?.error || e.message); }
    setLoading(false);
  }, [ate]);
  useEffect(() => { carregar(); }, [carregar]);

  const gerar = async () => {
    setGerando(true); setErro(null);
    try {
      const c = await liquidacoesGerais.gerarEstafetas(ate);
      setOk(`${c.length} liquidação(ões) criada(s)`);
      carregar();
    } catch (e: any) { setErro(e?.response?.data?.error || e.message); }
    setGerando(false);
  };
  const mudar = async (l: LiquidacaoEstafeta, accao: 'PAGAR' | 'ANULAR', referencia?: string) => {
    setErro(null);
    try {
      await liquidacoesGerais.mudarEstadoEstafeta(l.id, accao, referencia);
      setOk(accao === 'PAGAR' ? 'Marcada como paga' : 'Anulada — as entregas voltam a estar por liquidar');
      setPagar(null); setRef('');
      carregar();
    } catch (e: any) { setErro(e?.response?.data?.error || e.message); }
  };
  const total = pend.reduce((a, e) => a + e.valor, 0);

  return (
    <>
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>{erro}</Alert>}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField size="small" type="date" label="Semana até (inclusive)" value={ate}
            onChange={(e) => setAte(e.target.value)} InputLabelProps={{ shrink: true }} helperText="Por omissão, o último domingo" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{cve(total)}</Typography>
            <Typography variant="body2" color="text.secondary">
              {pend.reduce((a, e) => a + e.n, 0)} entregas por liquidar, de {pend.length} estafeta(s) — o valor por entrega vem do contrato Food de cada restaurante
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={carregar}>Actualizar</Button>
          <Button variant="contained" startIcon={gerando ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
            disabled={gerando || pend.length === 0} onClick={gerar}>
            Gerar liquidações da semana
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Estafeta</TableCell><TableCell>Telefone</TableCell>
              <TableCell align="right">Entregas</TableCell><TableCell align="right">A pagar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : pend.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>Nenhuma entrega por liquidar até {ate}</TableCell></TableRow>
            ) : pend.map((e) => (
              <TableRow key={e.motoboyId} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{e.nome}</Typography></TableCell>
                <TableCell>{e.telefone ?? '—'}</TableCell>
                <TableCell align="right">{e.n}</TableCell>
                <TableCell align="right"><b>{cve(e.valor)}</b></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>Liquidações dos estafetas</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Estafeta</TableCell><TableCell>Semana até</TableCell>
              <TableCell align="right">Entregas</TableCell><TableCell align="right">Valor</TableCell>
              <TableCell>Estado</TableCell><TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {lista.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>Ainda não há liquidações</TableCell></TableRow>}
            {lista.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>{l.nome ?? l.motoboy_id}</TableCell>
                <TableCell>{String(l.periodo_fim).slice(0, 10)}</TableCell>
                <TableCell align="right">{l.n_entregas}</TableCell>
                <TableCell align="right"><b>{cve(l.valor_cve)}</b></TableCell>
                <TableCell>{estadoChip(l.estado)}{l.referencia_banco && <Typography variant="caption" display="block">ref. {l.referencia_banco}</Typography>}</TableCell>
                <TableCell align="right">
                  {l.estado === 'RASCUNHO' && <>
                    <Tooltip title="Marcar como paga"><IconButton size="small" color="success" onClick={() => setPagar(l)}><Payments fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Anular"><IconButton size="small" color="error" onClick={() => mudar(l, 'ANULAR')}><Cancel fontSize="small" /></IconButton></Tooltip>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!pagar} onClose={() => setPagar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar como paga</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{pagar && `${pagar.nome ?? ''} · ${cve(pagar.valor_cve)}`}</Typography>
          <TextField fullWidth autoFocus label="Referência da transferência" value={ref} onChange={(e) => setRef(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagar(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!ref.trim()} onClick={() => pagar && mudar(pagar, 'PAGAR', ref)}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!ok} autoHideDuration={3000} onClose={() => setOk('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success">{ok}</Alert>
      </Snackbar>
    </>
  );
}
